//
//  Birth.tsx
//  TrajeXY Signet Server
//
//  Copyright © 2026 TrajeXY. All rights reserved.
//
//  Created: September 23, 2026
//  Author: Bob Keathley
//
//  Notebook birth wizard (spec 6c, 6d). Builds the preview answers body from
//  the ten intake questions. never/escalate lines split on the first ' -- '.
//
import { useCallback, useEffect, useState } from 'react'
import { api } from '@shared/api/client'
import { isOk } from '@shared/api/envelope'
import { checkIntakeAnswer, INTAKE_QUESTIONS, overreach, type CeilingCheck, type IntakeQuestion } from '@shared/api/mandate'
import { TITLE_MAX, titleError, type BirthCertMint, type BirthCertSummary, CanonBirth, CanonBirthExistsDetails, CanonPreview, CanonSectors, IntakeAnswers, IntakeCeilingDetails, MandateTemplate, NotebookPreview } from '@shared/api/types'
import { atLeast, type Session, type TenantRole } from '@shared/auth'
import { href } from '@shared/router'
import { CanonBirthPanel, CanonInterview } from '../components/CanonInterview'

export { CanonBirthPanel, CanonInterview }

type Path = 'template' | 'intake'
type Answers = Record<string, unknown>

/** Free-text `never`/`escalate` lines -> `{pattern, reason}`, splitting on the first " -- ".
 * The intake wizard collects one prohibition/escalation per line; the `answers` body (§6c) wants
 * structured pairs. A line with no " -- " becomes `{pattern: line, reason: ""}`. */
export function toRules(v: unknown): { pattern: string; reason: string }[] {
  if (!Array.isArray(v)) return []
  return (v as string[]).map((line) => {
    const i = line.indexOf(' -- ')
    return i === -1 ? { pattern: line, reason: '' } : { pattern: line.slice(0, i), reason: line.slice(i + 4) }
  })
}

/** Builds the `answers` body of `POST /v1/notebooks/preview` (§6c) from the intake wizard's
 * per-question state -- keyed by the ten intake-schema question ids, exactly as the spec names them. */
export function toIntakeAnswers(a: Answers): IntakeAnswers {
  return {
    agent: (a.agent as string) ?? '',
    role: (a.role as string) ?? '',
    scopes: (a.scopes as string[]) ?? [],
    never: toRules(a.never),
    escalate: toRules(a.escalate),
    budget: (a.budget as IntakeAnswers['budget']) ?? {},
    environments: (a.environments as string[]) ?? [],
    model: (a.model as string) ?? '',
    effort: (a.effort as string) ?? '',
    contracts: (a.contracts as string[]) ?? [],
  }
}

/** The ten-question intake state: each answer is checked server-side as it is given (§6b, ceiling of record);
 * an answer that over-reaches is refused with its reason and never stored. Shared by `/birth` and the
 * per-agent Notebook form on a Project page. */
export function useIntakeAnswers(role: TenantRole, token: string) {
  const [answers, setAnswers] = useState<Answers>({})
  const [checks, setChecks] = useState<Record<string, CeilingCheck>>({})
  const answerQuestion = useCallback(async (q: IntakeQuestion, value: unknown) => {
    const pre = overreach(q, value, role)
    if (pre) { setChecks((c) => ({ ...c, [q.id]: { allowed: false, reason: pre, details: { question_id: q.id, role, ceiling: null, requested: value, over: null } } })); return }
    setChecks((c) => ({ ...c, [q.id]: { allowed: true } })) // optimistic while the server-truth call is in flight
    const verdict = await checkIntakeAnswer(q.id, value, token)
    setChecks((c) => ({ ...c, [q.id]: verdict }))
    if (verdict.allowed) setAnswers((a) => ({ ...a, [q.id]: value }))
  }, [role, token])
  return { answers, checks, setChecks, answerQuestion }
}

/**
 * `/birth` -- name the agent, then either pick a template (platform/tenant, with fork) or answer
 * the ten. Every answer is checked against the signed-in user's authority ceiling as it is given;
 * an over-reach is refused right there, with a reason, never later at mint. `POST
 * /v1/mandates/intake/check` (spec §6b) is the ceiling of record; `overreach()` only pre-checks
 * instantly while that call is in flight. Minting itself is out of scope for this workstream
 * (Step 4) -- the flow stops at a disabled "Mint" button.
 */
export default function Birth({ session }: { session: Session }) {
  // The tenant app never renders this page for a platform-plane session (App.tsx's `isPlatformRole`
  // gate runs first), so this is always a tenant role in practice.
  const role = session.user.role as TenantRole
  const [agent, setAgent] = useState('')
  const [path, setPath] = useState<Path | null>(null)

  // -- template path --------------------------------------------------------------------------
  const [templates, setTemplates] = useState<MandateTemplate[] | 'unavailable' | null>(null)
  const [templateId, setTemplateId] = useState('')
  const [fork, setFork] = useState(false)
  const [forkBusy, setForkBusy] = useState(false)
  const [forkTitle, setForkTitle] = useState('')
  const [forkDesc, setForkDesc] = useState('')
  const [forkedId, setForkedId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (path !== 'template' || templates !== null) return
    api<{ items: MandateTemplate[] }>('/mandates/templates', {}, session.token).then((r) => {
      if (isOk(r)) setTemplates(r.data.items)
      else setTemplates('unavailable')
    })
  }, [path, templates, session.token])
  const selected = templates !== 'unavailable' && templates ? templates.find((t) => t.id === templateId) ?? null : null

  async function forkTemplate() {
    if (!selected || titleError(forkTitle) || !forkDesc.trim()) return
    setForkBusy(true); setError(null)
    const r = await api<MandateTemplate>('/mandates/templates', { method: 'POST', body: JSON.stringify({ fork_from: selected.id, title: forkTitle, description: forkDesc }) }, session.token)
    setForkBusy(false)
    if (!isOk(r)) { setError(r.error.message); return }
    setForkedId(r.data.id)
  }
  const previewTemplateId = fork ? forkedId : (selected?.id ?? null)

  // -- intake path: each answer is checked server-side (§6b, ceiling of record) ----------------
  const { answers, checks, setChecks, answerQuestion } = useIntakeAnswers(role, session.token)

  const canPreview = path === 'template' ? !!previewTemplateId : path === 'intake' ? INTAKE_QUESTIONS.every((q) => q.id in answers) : false

  // -- effective-policy preview -------------------------------------------------------------------
  const [preview, setPreview] = useState<NotebookPreview | 'unavailable' | null>(null)
  const [previewBusy, setPreviewBusy] = useState(false)
  async function runPreview() {
    setPreviewBusy(true); setError(null)
    const body = path === 'template'
      ? { template_id: previewTemplateId, ...(agent.trim() ? { agent: agent.trim() } : {}) }
      : { answers: toIntakeAnswers(answers) }
    const r = await api<NotebookPreview>('/notebooks/preview', { method: 'POST', body: JSON.stringify(body) }, session.token)
    setPreviewBusy(false)
    if (isOk(r)) { setPreview(r.data); return }
    if (r.error.code === 'authority_ceiling') {
      // The bypass guard fired -- preview enforces the same per-question ceiling as intake/check.
      // Surface the refusal at the question it names, exactly like a direct intake/check call would.
      const d = r.error.details as IntakeCeilingDetails
      setChecks((c) => ({ ...c, [d.question_id]: { allowed: false, reason: r.error.message, details: d } }))
      setError(`Refused at "${d.question_id}": ${r.error.message}`)
      setPreview(null)
      return
    }
    if (r.error.code === 'validation_error') setError(r.error.message)
    setPreview('unavailable')
  }

  // -- optional canon interview -------------------------------------------------------------------
  const [canonOn, setCanonOn] = useState(false)
  const [sectors, setSectors] = useState<CanonSectors | 'unavailable' | null>(null)
  const [sectorId, setSectorId] = useState('')
  const [canonAnswers, setCanonAnswers] = useState<string[]>([])
  const [canon, setCanon] = useState<CanonPreview | 'unavailable' | null>(null)

  useEffect(() => {
    if (!canonOn || sectors !== null) return
    api<CanonSectors>('/canon/sectors', {}, session.token).then((r) => {
      if (isOk(r)) { setSectors(r.data); if (r.data.sectors[0]) { setSectorId(r.data.sectors[0].sector); setCanonAnswers(new Array(r.data.axes.length).fill('')) } }
      else setSectors('unavailable')
    })
  }, [canonOn, sectors, session.token])
  const sector = sectors !== 'unavailable' && sectors ? sectors.sectors.find((s) => s.sector === sectorId) ?? null : null

  async function runCanonPreview(nextAnswers: string[]) {
    if (nextAnswers.some((a) => !a)) return // all nine required before the server will accept it
    const r = await api<CanonPreview>('/canon/preview', { method: 'POST', body: JSON.stringify({ sector: sectorId, agent_name: agent || 'unnamed', answers: nextAnswers }) }, session.token)
    setCanon(isOk(r) ? r.data : 'unavailable')
  }

  // -- store a canon birth record (`POST /v1/canon/births`, owner/security-admin only) ----------
  const [canonBirth, setCanonBirth] = useState<CanonBirth | 'forbidden' | 'unavailable' | CanonBirthExistsDetails | null>(null)
  const [canonBirthBusy, setCanonBirthBusy] = useState(false)
  async function runCanonBirth() {
    if (canonAnswers.some((a) => !a)) return
    setCanonBirthBusy(true)
    const r = await api<CanonBirth>('/canon/births', { method: 'POST', body: JSON.stringify({ sector: sectorId, agent_name: agent || 'unnamed', answers: canonAnswers }) }, session.token)
    setCanonBirthBusy(false)
    if (isOk(r)) { setCanonBirth(r.data); return }
    if (r.error.code === 'forbidden') { setCanonBirth('forbidden'); return }
    // A repeated, identical birth (same sector/agent_name/answers/accountability) is never an
    // overwrite -- the server 409s and names the original record; point the human at it.
    if (r.error.code === 'canon_birth_exists') { setCanonBirth(r.error.details as CanonBirthExistsDetails); return }
    setCanonBirth('unavailable')
  }

  return (
    <>
      <h2>Birth an agent</h2>
      <p className="hint">Name it, then pick a template or answer the ten. An answer that exceeds
        your authority ({role}) is refused right here, with a reason -- never later, at mint.</p>
      {error && <p className="err" role="alert">{error}</p>}

      <label htmlFor="b-agent">Agent name (notebook id)</label>
      <input id="b-agent" value={agent} onChange={(e) => setAgent(e.target.value)} placeholder="agent-c-ui" />

      <h2>Path</h2>
      <div className="row">
        <button className={`btn ${path === 'template' ? '' : 'ghost'}`} onClick={() => setPath('template')}>Pick a template</button>
        <button className={`btn ${path === 'intake' ? '' : 'ghost'}`} onClick={() => setPath('intake')}>Answer the ten</button>
      </div>

      {path === 'template' && (
        <>
          <h2>Template</h2>
          {templates === null && <p className="hint">Loading templates…</p>}
          {templates === 'unavailable' && <p className="hint">The template catalogue isn't reachable right now. Try again, or answer the ten instead.</p>}
          {Array.isArray(templates) && templates.length === 0 && <p className="hint">No templates assigned. Ask the platform to assign one, or answer the ten instead.</p>}
          {Array.isArray(templates) && templates.length > 0 && (
            <>
              <select value={templateId} onChange={(e) => { setTemplateId(e.target.value); setFork(false); setForkedId(null) }}>
                <option value="">— choose —</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.title ? `${t.title} — ` : ''}{t.namespace}/{t.name} v{t.version} ({t.source})</option>)}
              </select>
              {selected && (
                <>
                  <p id="b-title" aria-label="Title (from the template)"><span className="hint">Title (from the template, read-only): </span>{selected.title || <span className="hint">(untitled)</span>} <span className="hint">· source template </span><span className="mono">{selected.namespace}/{selected.name}@{selected.version}</span></p>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, textTransform: 'none' }}>
                    <input type="checkbox" style={{ width: 'auto' }} checked={fork} onChange={(e) => { setFork(e.target.checked); setForkedId(null); if (e.target.checked && selected) { setForkTitle(selected.title ?? ''); setForkDesc(selected.description ?? '') } }}
                      disabled={!atLeast(role, 'security-admin')} />
                    <span className="hint">Fork -- create a tenant copy recording this template as the upstream, rather than birthing it unchanged.
                      {!atLeast(role, 'security-admin') && ' (owner/security-admin only)'}</span>
                  </label>
                  {fork && !forkedId && (
                    <>
                      <label htmlFor="fork-title">Title (one line) <span className="hint" data-testid="fork-title-count">{forkTitle.length}/{TITLE_MAX}</span></label>
                      <input id="fork-title" value={forkTitle} maxLength={TITLE_MAX} onChange={(e) => setForkTitle(e.target.value.replace(/[\r\n]/g, ' '))} />
                      <label htmlFor="fork-desc">Description (markdown)</label>
                      <textarea id="fork-desc" rows={3} value={forkDesc} onChange={(e) => setForkDesc(e.target.value)} />
                      {selected && !selected.title && <p className="hint">The upstream template has no title (an older row); a fork needs one.</p>}
                    </>
                  )}
                  {fork && !forkedId && (
                    <p><button className="btn ghost" disabled={forkBusy || !!titleError(forkTitle) || !forkDesc.trim()} onClick={forkTemplate}>{forkBusy ? 'Forking…' : 'Fork this template'}</button></p>
                  )}
                  {fork && forkedId && <p className="hint">Forked -- new tenant template <span className="mono">{forkedId.slice(0, 8)}…</span> ready to preview.</p>}
                </>
              )}
              {selected && selected.source === 'tenant' && !fork && atLeast(role, 'security-admin') && (
                <TemplateEditor template={selected} token={session.token} />
              )}
            </>
          )}
        </>
      )}

      {path === 'intake' && (
        <>
          <h2>The ten</h2>
          {INTAKE_QUESTIONS.map((q) => (
            <IntakeRow key={q.id} q={q} value={answers[q.id]} onAnswer={(v) => answerQuestion(q, v)} check={checks[q.id]} role={role} />
          ))}
        </>
      )}

      {path && (
        <p style={{ marginTop: 16 }}>
          <button className="btn" disabled={!canPreview || previewBusy} onClick={runPreview}>
            {previewBusy ? 'Compiling…' : 'Preview effective policy'}
          </button>
          {path === 'template' && fork && !forkedId && <span className="hint" style={{ marginLeft: 10 }}>Fork the template first.</span>}
          {path === 'intake' && !canPreview && <span className="hint" style={{ marginLeft: 10 }}>Answer every question (or accept the refusal) to continue.</span>}
        </p>
      )}

      {preview && <PreviewPanel preview={preview} />}

      {preview && preview !== 'unavailable' && (
        <>
          <h2>Canon interview <span className="hint">(optional)</span></h2>
          {!canonOn && <button className="btn ghost" onClick={() => setCanonOn(true)}>Run the nine-question canon interview</button>}
          {canonOn && (
            <CanonInterview
              sectors={sectors}
              sector={sector}
              sectorId={sectorId}
              onSector={(s) => { setSectorId(s); setCanonAnswers(new Array(sectors !== 'unavailable' && sectors ? sectors.axes.length : 0).fill('')); setCanon(null) }}
              answers={canonAnswers}
              onAnswer={(i, v) => {
                const next = [...canonAnswers]; next[i] = v
                setCanonAnswers(next)
                runCanonPreview(next)
              }}
              canon={canon}
              role={role}
              onBirth={runCanonBirth}
              birthBusy={canonBirthBusy}
              birth={canonBirth}
            />
          )}
        </>
      )}

      {preview && preview !== 'unavailable' && (
        <MintPanel
          session={session}
          role={role}
          path={path}
          previewTemplateId={previewTemplateId}
          agent={agent}
          answers={answers}
          canonBirth={canonBirth}
          canon={canon}
          canonOn={canonOn}
          sectorId={sectorId}
          canonAnswers={canonAnswers}
        />
      )}

      <p style={{ marginTop: 24 }}><a href={href('/births')}>See all birth certificates ›</a></p>
    </>
  )
}

/** Builds the exact `POST /v1/notebooks/birth-cert` body (§6d): `{template_id | answers, agent,
 * kind, team?, canon_birth_id? | canon?, note?}` -- `agent`/`kind` always present, the rest only
 * when given. Split out from `MintPanel.runMint` so the wire shape is directly testable. */
export function buildMintBody({ path, previewTemplateId, agent, answers, team, note, canonBirthId, inlineCanon }: {
  path: Path
  previewTemplateId: string | null
  agent: string
  answers: Answers
  team: string
  note: string
  canonBirthId: string | null
  inlineCanon: { sector: string; answers: string[] } | null
}): Record<string, unknown> {
  const mintAgent = path === 'template' ? agent.trim() : toIntakeAnswers(answers).agent
  return {
    ...(mintAgent ? { agent: mintAgent } : {}),
    kind: 'agent',
    ...(path === 'template' ? { template_id: previewTemplateId } : { answers: toIntakeAnswers(answers) }),
    ...(team.trim() ? { team: team.trim() } : {}),
    ...(canonBirthId ? { canon_birth_id: canonBirthId } : inlineCanon ? { canon: inlineCanon } : {}),
    ...(note.trim() ? { note: note.trim() } : {}),
  }
}

/**
 * Mint (§6d, team-admin+, Bob's ruling). Signs nothing -- validates the request and hands back a one-time
 * code; the template's latest non-retired version is compiled and signed only at redeem (B3).
 * `canon_birth_id` (a stored canon record) and inline `canon` are mutually exclusive -- a stored
 * record wins if both are available. QUARANTINE-REVIEW is shown as a badge, never a blocker (B4).
 */
export function MintPanel({ session, role, path, previewTemplateId, agent, answers, canonBirth, canon, canonOn, sectorId, canonAnswers }: {
  session: Session
  role: TenantRole
  path: Path | null
  previewTemplateId: string | null
  agent: string
  answers: Answers
  canonBirth: CanonBirth | 'forbidden' | 'unavailable' | CanonBirthExistsDetails | null
  canon: CanonPreview | 'unavailable' | null
  canonOn: boolean
  sectorId: string
  canonAnswers: string[]
}) {
  const [note, setNote] = useState('')
  const [team, setTeam] = useState('')
  const [busy, setBusy] = useState(false)
  const [mint, setMint] = useState<BirthCertMint | null>(null)
  const [err, setErr] = useState<{ code: string; message: string; details?: unknown } | null>(null)
  const [now, setNow] = useState(() => Date.now() / 1000)
  const [birthBusy, setBirthBusy] = useState(false)
  const [born, setBorn] = useState<BirthCertSummary | null>(null)
  const [birthErr, setBirthErr] = useState<{ code: string; message: string; details?: unknown } | null>(null)

  useEffect(() => {
    if (!mint) return
    const t = setInterval(() => setNow(Date.now() / 1000), 1000)
    return () => clearInterval(t)
  }, [mint])

  const canonBirthId = canonBirth && typeof canonBirth === 'object' && 'id' in canonBirth ? canonBirth.id : null
  const canonStatus = canonBirth && typeof canonBirth === 'object' && 'record' in canonBirth
    ? canonBirth.record.status
    : canon && canon !== 'unavailable' ? canon.record.status : null
  const inlineCanon = !canonBirthId && canonOn && sectorId && canonAnswers.length > 0 && canonAnswers.every(Boolean)
    ? { sector: sectorId, answers: canonAnswers }
    : null

  async function runMint() {
    if (!path) return
    setBusy(true); setErr(null)
    const body = buildMintBody({ path, previewTemplateId, agent, answers, team, note, canonBirthId, inlineCanon })
    const r = await api<BirthCertMint>('/notebooks/birth-cert', { method: 'POST', body: JSON.stringify(body) }, session.token)
    setBusy(false)
    if (isOk(r)) { setMint(r.data); return }
    setErr(r.error)
  }

  // §6d `POST /v1/notebooks/birth`: the same body as mint, signed now with the tenant key.
  async function runBirth() {
    if (!path) return
    setBirthBusy(true); setBirthErr(null)
    const body = buildMintBody({ path, previewTemplateId, agent, answers, team, note, canonBirthId, inlineCanon })
    const r = await api<BirthCertSummary>('/notebooks/birth', { method: 'POST', body: JSON.stringify(body) }, session.token)
    setBirthBusy(false)
    if (isOk(r)) { setBorn(r.data); return }
    setBirthErr(r.error)
  }

  if (!atLeast(role, 'team-admin')) {
    return (
      <>
        <h2>Birth Notebook</h2>
        <p className="hint">Only team-admin/owner/security-admin may birth a Notebook -- your role ({role}) may preview but not birth.</p>
      </>
    )
  }

  return (
    <>
      <h2>Birth Notebook</h2>
      <p className="hint">
        Signs the Notebook now with your organisation's key. It goes to Macs in your next policy release -- there is no code to copy.{' '}
        {canonStatus === 'QUARANTINE-REVIEW' && <span className="badge b-watch">QUARANTINE-REVIEW</span>}
      </p>
      {!born && (
        <>
          <label htmlFor="mint-team">Team <span className="hint">(optional)</span></label>
          <input id="mint-team" value={team} onChange={(e) => setTeam(e.target.value)} placeholder="optional team label" />
          <label htmlFor="mint-note">Note <span className="hint">(optional)</span></label>
          <input id="mint-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="why this agent, for the record" />
          <p><button className="btn" disabled={birthBusy} onClick={runBirth}>{birthBusy ? 'Signing…' : 'Birth Notebook'}</button></p>
          {birthErr && <MintError err={birthErr} />}
        </>
      )}
      {born && (
        <div className="card" role="status">
          <div className="l">Born and signed</div>
          <p className="mono">{born.agent}{born.template ? ` · ${born.template.namespace}/${born.template.name}@${born.cert?.template_version ?? born.template.version ?? '—'}` : ''}</p>
          <p className="hint">It will be in your next policy release. Publish one from <a href={href('/policy')}>Policy ›</a></p>
          <p style={{ marginTop: 8 }}><a href={href('/births')}>See it on Births ›</a></p>
        </div>
      )}

      <h3 style={{ marginTop: 24 }}>Get a code for an older Mac</h3>
      <p className="hint">
        Only for a Mac still on the 3.7.1 engine. This signs nothing now -- the template's latest
        version is compiled when the code is redeemed on that Mac.
      </p>
      {!mint && (
        <>
          <p><button className="btn ghost" disabled={busy} onClick={runMint}>{busy ? 'Minting…' : 'Mint birth certificate'}</button></p>
          {err && <MintError err={err} />}
        </>
      )}
      {mint && <MintResult mint={mint} now={now} />}
    </>
  )
}

export function MintError({ err }: { err: { code: string; message: string; details?: unknown } }) {
  if (err.code === 'template_retired') return <p className="err" role="alert">410 template_retired -- no non-retired version left to birth from: {err.message}</p>
  // §6d, lead decision (a): mint now ceiling-checks a template_id body too, against the resolved
  // latest version's compiled mandate -- same 422 as an over-ceiling answers-path mint.
  if (err.code === 'authority_ceiling') return <p className="err" role="alert">422 authority_ceiling -- refused: {err.message}</p>
  if (err.code === 'validation_error') return <p className="err" role="alert">422 validation_error -- {err.message}</p>
  if (err.code === 'template_not_assigned') return <p className="err" role="alert">403 template_not_assigned -- the platform hasn't assigned this template to your organisation. Ask the platform to assign it.</p>
  if (err.code === 'key_unreadable') return <p className="err" role="alert">503 key_unreadable -- the tenant signing key couldn't be read. Nothing was stored.</p>
  if (err.code === 'not_found') return <p className="err" role="alert">404 not_found -- that template no longer resolves.</p>
  if (err.code === 'forbidden') return <p className="err" role="alert">403 forbidden -- your role may not mint.</p>
  // §6d: SIGNET_BIRTH_KEK missing or malformed fails mint closed, same as redeem and the keys route.
  if (err.code === 'kek_unavailable') return <p className="err" role="alert">503 kek_unavailable -- the tenant signing key isn't configured. Nothing was stored.</p>
  return <p className="err" role="alert">{err.message}</p>
}

export function MintResult({ mint, now }: { mint: BirthCertMint; now: number }) {
  const [copied, setCopied] = useState(false)
  const remaining = Math.max(0, Math.round(mint.expires_at - now))
  const mm = Math.floor(remaining / 60)
  const ss = String(remaining % 60).padStart(2, '0')
  return (
    <div className="card" role="status">
      <div className="l">Birth code</div>
      <p className="mono" style={{ fontSize: 20 }}>{mint.code}</p>
      <p className="hint">{remaining > 0 ? `Expires in ${mm}:${ss}` : 'Expired -- mint again.'}</p>
      <p className="hint">The template's latest version is compiled when this code is redeemed -- nothing is signed yet.</p>
      <p className="mono" style={{ userSelect: 'all' }}>{mint.command}</p>
      <p><button className="btn ghost sm" onClick={() => { navigator.clipboard?.writeText(mint.command); setCopied(true) }}>{copied ? 'Copied' : 'Copy command'}</button></p>
      <p style={{ marginTop: 8 }}><a href={href('/births')}>See it on #/births ›</a></p>
    </div>
  )
}

/**
 * PATCH a tenant-sourced template (§6b hardening). Never mutates the row -- inserts a new version.
 * 409 on a retired template's latest version, 422 on a `trxy/` namespace or malformed mandate.
 * Platform templates never reach this component (mint UI only offers it for `source: 'tenant'`).
 */
export function TemplateEditor({ template, token }: { template: MandateTemplate; token: string | null }) {
  const [mandate, setMandate] = useState(() => JSON.stringify(template.mandate, null, 2))
  const [version, setVersion] = useState('')
  const [title, setTitle] = useState(template.title ?? '')
  const [description, setDescription] = useState(template.description ?? '')
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<MandateTemplate | null>(null)
  const [err, setErr] = useState<{ code: string; message: string; details?: unknown } | null>(null)

  async function patch() {
    const te = titleError(title)
    if (te || !description.trim()) { setErr({ code: 'validation_error', message: te ?? 'A description is required.' }); return }
    setBusy(true); setErr(null); setResult(null)
    let parsed: unknown
    try { parsed = JSON.parse(mandate) } catch { setBusy(false); setErr({ code: 'validation_error', message: 'Not valid JSON.' }); return }
    const body: Record<string, unknown> = { mandate: parsed, title, description, ...(version.trim() ? { version: version.trim() } : {}) }
    const r = await api<MandateTemplate>(`/mandates/templates/${template.id}`, { method: 'PATCH', body: JSON.stringify(body) }, token)
    setBusy(false)
    if (isOk(r)) { setResult(r.data); return }
    setErr(r.error)
  }

  const trxyNamespace = err?.code === 'validation_error' && (err.details as { namespace?: string } | undefined)?.namespace?.startsWith('trxy/')

  return (
    <div className="card" style={{ marginTop: 12 }}>
      <div className="l">Edit {template.namespace}/{template.name} v{template.version}</div>
      <p className="hint">PATCH never mutates this row -- it inserts a new version and the old one
        stays resolvable forever. A retired template accepts no further versions (fork it instead),
        and a namespace starting <span className="mono">trxy/</span> is reserved for platform templates.</p>
      <label htmlFor="te-title">Title (one line) <span className="hint" data-testid="te-title-count">{title.length}/{TITLE_MAX}</span></label>
      <input id="te-title" value={title} maxLength={TITLE_MAX} onChange={(e) => setTitle(e.target.value.replace(/[\r\n]/g, ' '))} />
      <label htmlFor="te-desc">Description (markdown)</label>
      <textarea id="te-desc" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
      <label htmlFor="te-mandate">Mandate JSON</label>
      <textarea id="te-mandate" rows={10} style={{ width: '100%', font: 'inherit', background: 'var(--bg)', color: 'var(--txt)', border: '1px solid var(--line)', borderRadius: 8, padding: 9 }}
        value={mandate} onChange={(e) => setMandate(e.target.value)} />
      <label htmlFor="te-version">Version <span className="hint">(optional -- else the patch component auto-bumps)</span></label>
      <input id="te-version" value={version} onChange={(e) => setVersion(e.target.value)} placeholder="1.0.1" />
      <p><button className="btn" disabled={busy} onClick={patch}>{busy ? 'Saving…' : 'Save as new version'}</button></p>
      {err?.code === 'template_retired' && <p className="err" role="alert">409 template_retired -- the latest version of this template is retired; fork it instead.</p>}
      {err?.code === 'validation_error' && trxyNamespace && <p className="err" role="alert">422 validation_error -- trxy/ is reserved for platform templates.</p>}
      {err?.code === 'validation_error' && !trxyNamespace && <p className="err" role="alert">422 validation_error -- {err.message}</p>}
      {err && err.code !== 'template_retired' && err.code !== 'validation_error' && <p className="err" role="alert">{err.message}</p>}
      {result && (
        <p className="hint" role="status">Saved as new version <span className="mono">{result.version}</span> (id <span className="mono">{result.id.slice(0, 8)}…</span>). The prior version stays resolvable forever.</p>
      )}
    </div>
  )
}

export function IntakeRow({ q, value, onAnswer, check, role }: { q: IntakeQuestion; value: unknown; onAnswer: (v: unknown) => void; check?: CeilingCheck; role?: string }) {
  const refusal = check && check.allowed === false ? check.reason : null
  const defaulted = check && check.allowed === true ? check.defaulted : null
  if (q.type === 'string') {
    return (
      <div>
        <label htmlFor={`q-${q.id}`}>{q.prompt}</label>
        <input id={`q-${q.id}`} defaultValue={value as string ?? ''} onBlur={(e) => onAnswer(e.target.value)} />
        {refusal && <p className="err" role="alert">{refusal}</p>}
      </div>
    )
  }
  if (q.type === 'multi' && q.options) {
    const sel = new Set((value as string[]) ?? [])
    return (
      <div>
        <label>{q.prompt}</label>
        <div className="row" style={{ flexWrap: 'wrap' }}>
          {q.options.map((o) => (
            <label key={o} style={{ display: 'flex', alignItems: 'center', gap: 6, flex: '0 0 auto', textTransform: 'none' }}>
              <input type="checkbox" style={{ width: 'auto' }} checked={sel.has(o)}
                onChange={(e) => { const next = new Set(sel); if (e.target.checked) next.add(o); else next.delete(o); onAnswer([...next]) }} />
              <span className="mono">{o}</span>
            </label>
          ))}
        </div>
        {refusal && <p className="err" role="alert">{refusal}</p>}
      </div>
    )
  }
  if (q.type === 'enum' && q.options) {
    return (
      <div>
        <label htmlFor={`q-${q.id}`}>{q.prompt}</label>
        <select id={`q-${q.id}`} value={value as string ?? ''} onChange={(e) => onAnswer(e.target.value)}>
          <option value="">— choose —</option>
          {q.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        {refusal && <p className="err" role="alert">{refusal}</p>}
      </div>
    )
  }
  if (q.type === 'object') {
    // Currently only `budget` is an object question: {usd_per_day, tokens_per_session}.
    const b = (value as { usd_per_day?: number; tokens_per_session?: number }) ?? {}
    return (
      <div>
        <label>{q.prompt}</label>
        <div className="row">
          <input type="number" placeholder="USD / day" defaultValue={b.usd_per_day ?? ''} onBlur={(e) => onAnswer({ ...b, usd_per_day: Number(e.target.value) || 0 })} />
          <input type="number" placeholder="Tokens / session" defaultValue={b.tokens_per_session ?? ''} onBlur={(e) => onAnswer({ ...b, tokens_per_session: Number(e.target.value) || 0 })} />
        </div>
        {defaulted && <p className="hint">No amount given -- defaulted to your {role ?? 'role'} ceiling: ${defaulted.usd_per_day}/day.</p>}
        {refusal && <p className="err" role="alert">{refusal}</p>}
      </div>
    )
  }
  // `rules` -- free-text list, one rule per line.
  return (
    <div>
      <label htmlFor={`q-${q.id}`}>{q.prompt}</label>
      <textarea id={`q-${q.id}`} rows={2} style={{ width: '100%', background: 'var(--bg)', color: 'var(--txt)', border: '1px solid var(--line)', borderRadius: 8, padding: 9, font: 'inherit' }}
        defaultValue={((value as string[]) ?? []).join('\n')}
        onBlur={(e) => onAnswer(e.target.value.split('\n').map((s) => s.trim()).filter(Boolean))} />
      {refusal && <p className="err" role="alert">{refusal}</p>}
    </div>
  )
}

export function PreviewPanel({ preview }: { preview: NotebookPreview | 'unavailable' }) {
  return (
    <>
      <h2>Effective policy</h2>
      {preview === 'unavailable' && <p className="hint">The preview couldn't be compiled -- check the template still resolves and try again.</p>}
      {preview !== 'unavailable' && (
        <>
          <p className="hint">Source: <span className="mono">{preview.source}</span></p>
          <table>
            <thead><tr><th>Key</th><th>Rule</th><th>From</th><th>File</th></tr></thead>
            <tbody>
              {preview.rules.length === 0 && <tr><td colSpan={4} className="hint">No rules compiled.</td></tr>}
              {preview.rules.map((r) => {
                // §6c: a missing budget answer defaults to the role ceiling; the server marks the
                // rule's provenance as `"default: <role> ceiling"` rather than an intake label so
                // it never reads as the human's own answer.
                const defaulted = r.file.startsWith('default:')
                return (
                  <tr key={r.key}>
                    <td className="mono">{r.key}</td>
                    <td className="mono">{JSON.stringify(r.rule)}</td>
                    <td><span className="pill">{r.level}</span></td>
                    <td className="hint mono">
                      {defaulted
                        ? <span className="badge b-watch">defaulted to your {r.file.replace(/^default:\s*/, '').replace(/\s*ceiling$/, '')} ceiling</span>
                        : r.file}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          {preview.dropped.length > 0 && (
            <>
              <h2>Dropped</h2>
              <p className="hint">A stricter ancestor won -- these attempted values never took effect.</p>
              <table>
                <thead><tr><th>Key</th><th>Kept</th><th>Kept from</th><th>Dropped</th><th>Dropped by</th><th>Why</th></tr></thead>
                <tbody>
                  {preview.dropped.map((d) => (
                    <tr key={d.key}>
                      <td className="mono">{d.key}</td>
                      <td className="mono">{JSON.stringify(d.kept)}</td>
                      <td className="mono">{d.kept_from}</td>
                      <td className="mono">{JSON.stringify(d.dropped)}</td>
                      <td className="mono">{d.dropped_from}</td>
                      <td>{d.why}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
          {preview.diff_vs_template && (
            <>
              <h2>Diff vs. the parent template</h2>
              <table>
                <thead><tr><th>Change</th><th>Key</th><th>Value</th></tr></thead>
                <tbody>
                  {Object.entries(preview.diff_vs_template.added).map(([k, v]) => <tr key={`a-${k}`}><td>added</td><td className="mono">{k}</td><td className="mono">{JSON.stringify(v)}</td></tr>)}
                  {Object.entries(preview.diff_vs_template.removed).map(([k, v]) => <tr key={`r-${k}`}><td>removed</td><td className="mono">{k}</td><td className="mono">{JSON.stringify(v)}</td></tr>)}
                  {Object.entries(preview.diff_vs_template.changed).map(([k, v]) => <tr key={`c-${k}`}><td>changed</td><td className="mono">{k}</td><td className="mono">{JSON.stringify(v.from)} → {JSON.stringify(v.to)}</td></tr>)}
                </tbody>
              </table>
            </>
          )}
        </>
      )}
    </>
  )
}

